import { useEffect, useState } from "react";
import { connect, mapProps, useForm } from "@formily/react";
import { IDealership } from "@rumble-it/common-lib/src/types/Dealership";
import { scrollToElement } from "@rumble-it/common-lib/src/helpers/scrollTo";
import { useStateMachine } from "little-state-machine";
import { Button, IRumbleFormScope } from ".";
import { IFormStep } from "@formily/antd-v5";
import { DealershipCard } from "./Dealership";
import { fetchDealershipSelector } from "@rumble-it/common-lib/src/api/Dealership";
import { Loading } from "../Loading";

interface DealershipSelectorProps {
  // Form-related props
  value?: IDealership | null;
  onChange?: (dealership: IDealership) => void;
  errors?: Array<{ messages: string[] }>;

  // Component behavior props
  mode?: "single" | string;
  scrollTo?: boolean | string;
  type: string;
}

export const DealershipSelector = (props: DealershipSelectorProps) => {
  const form = useForm();
  const { state } = useStateMachine();

  const [dealerships, setDealerships] = useState<IDealership[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showAllDealerships, setShowAllDealerships] = useState<boolean>(false);
  const [selectedDealership, setSelectedDealership] =
    useState<IDealership | null>(props.value || null);

  // Safely extract scope and fetchData
  const scope = (state as { scope?: IRumbleFormScope }).scope;
  const fetchData = scope?.fetchData;
  if (!fetchData) {
    throw new Error("fetchData function is not available");
  }
  const formStep = scope?.formStep as IFormStep;
  if (!formStep) {
    throw new Error("formStep function is not available");
  }
  const scrollTo = props.scrollTo ?? true;

  if (props.mode && props.mode === "single" && form?.values?.dealership) {
    const dealership = form.values.dealership;
    useEffect(() => {
      const fetchDealership = async () => {
        if (!dealership) {
          return;
        }

        setLoading(true);
        setError(null);

        try {
          const response = await fetchData({
            endpoint: `companies/${dealership.id}`,
          });

          if (response && response.data) {
            if (response?.data) {
              response.data.dealershipIri = response.data["@id"];
            }

            // Explicitly set the value in the form model to ensure it's included in form.values
            form.setValues({
              ...form.values,
              dealership: response.data,
            });

            setSelectedDealership(response.data);
            setDealerships([response.data]);
          } else {
            setError("Geen dealer gevonden");
          }
        } catch (err) {
          console.error("Error fetching dealers:", err);
          setError("Er is een fout opgetreden bij het ophalen van dealers");
        } finally {
          setLoading(false);
        }
      };

      fetchDealership();
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dealership]);
  } else {
    // Get values from form
    const postalCode = form.values.postalCode;
    const streetNumber = form.values.streetNumber;

    useEffect(() => {
      async function fetchDealerships() {
        setLoading(true);
        setError(null);

        try {
          const fetchedDealerships = await fetchDealershipSelector({
            postalCode,
            streetNumber,
            fetchData,
          });

          setDealerships(fetchedDealerships);
        } catch (err) {
          setError(err);
        } finally {
          setLoading(false);
        }
      }

      fetchDealerships();
    }, [postalCode, streetNumber, fetchData]);
  }

  const handleSelectDealership = (dealership: IDealership) => {
    const values = {
      ...form.values,
      dealership: dealership,
    };
    if (dealership.servicePartners && dealership.servicePartners[0]) {
      values.servicePartner = {
        servicePartnerIri: dealership.servicePartners[0],
      };
    }

    // Explicitly set the value in the form model to ensure it's included in form.values
    form.setValues(values);
    setSelectedDealership(dealership);

    // Pass the dealership.dealershipIri directly to onChange instead of selectedDealershipIri
    // which wouldn't have been updated yet when this line executes
    props.onChange(dealership);

    // If formStep is available, move to the next step
    if (formStep) {
      formStep.next();
      if (scrollTo !== false) {
        scrollToElement({
          element: scrollTo === true ? "#rumbledirect-form" : scrollTo,
        });
      }
    }
  };

  const toggleDealerships = () => {
    setShowAllDealerships(!showAllDealerships);
  };

  // Determine which dealers to show
  const visibleDealerships = () => {
    if (dealerships.length === 0) return [];
    if (dealerships.length === 1) return dealerships;

    return showAllDealerships ? dealerships : [dealerships[0]];
  };

  // Calculate how many partners are hidden
  const hiddenDealershipsCount =
    dealerships.length > 1 ? dealerships.length - 1 : 0;

  return (
    <div className="formily-service-partner-selector">
      {loading ? (
        <div className="flex flex-col gap-4 text-center p-4">
          <Loading />
        </div>
      ) : null}

      {error ? (
        <div className="text-center text-red-500 p-4">
          <p>
            Er is wat mis gegaan bij het zoeken naar vestigingen. Controleer het
            ingevoerde adres en probeer het opnieuw.
          </p>
        </div>
      ) : null}

      {!loading && !error && dealerships.length === 0 ? (
        <div className="text-center p-4">
          <p>Geen dealer bedrijven gevonden voor deze locatie.</p>
        </div>
      ) : null}

      {!loading && dealerships.length > 0 ? (
        <div className="space-y-4">
          {visibleDealerships().map((dealership) => (
            <DealershipCard
              key={dealership["@id"]}
              dealership={dealership}
              isSelected={
                selectedDealership?.dealershipIri === dealership.dealershipIri
              }
              onClick={handleSelectDealership}
            />
          ))}

          {hiddenDealershipsCount > 0 ? (
            <div className="text-center">
              <Button
                variant="outlined"
                className="font-bold text-sm tracking-normal"
                onClick={toggleDealerships}
              >
                {showAllDealerships
                  ? `${hiddenDealershipsCount} dealer bedrijven verbergen`
                  : `nog ${hiddenDealershipsCount} weergeven`}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {props.errors?.length > 0 ? (
        <div className="text-red-500 p-4">
          {props.errors.map((error, index) => (
            <div key={index}>{error.messages.join(", ")}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export const RumbleDealershipSelector = connect(
  DealershipSelector,
  mapProps((props: DealershipSelectorProps, field: any) => {
    return {
      ...props,
      value: props.value,
      onChange: props.onChange,
      invalid: field?.valid === false,
      errors: field?.errors,
    };
  }),
);

export default DealershipSelector;
